import { memo, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { CLAIM_TYPE_PROVIDER_CONFIG } from '@claim-os/shared';
import { GENERAL_DOCUMENTS, getRequiredDocuments } from '../requiredDocumentsConfig';

interface RequiredDocumentsListProps {
  /** Selected claim-type codes, in selection order. */
  selectedTypeCodes: string[];
}

interface DocumentColumn {
  /** Stable list key — 'GENERAL' or the claim-type code. */
  key: string;
  /** Group heading; source string kept normal-case (uppercased via CSS only). */
  heading: string;
  documents: readonly string[];
}

/**
 * Feature 033 — read-only "Required Documents" checklist shown above the upload
 * zone on the Submit Claim page. Renders an always-visible "General" group plus
 * one group per selected claim type, at most three groups per row (wraps).
 *
 * Owns no outer spacing — the parent positions it, so it reads correctly wherever
 * it sits in the documents section.
 *
 * Pure display: it reads the selected claim types only and never validates,
 * binds to the form, or tracks uploads. memo'd because it is a presentational
 * leaf re-rendered by a chatty parent (the submit form re-renders on every field
 * change); the derived column model is memoized for the same reason.
 */
const RequiredDocumentsList = memo(function RequiredDocumentsList({
  selectedTypeCodes,
}: RequiredDocumentsListProps) {
  const columns = useMemo<DocumentColumn[]>(
    () => [
      { key: 'GENERAL', heading: 'General', documents: GENERAL_DOCUMENTS },
      ...selectedTypeCodes.map((code) => ({
        key: code,
        heading: CLAIM_TYPE_PROVIDER_CONFIG[code]?.label ?? code,
        documents: getRequiredDocuments(code),
      })),
    ],
    [selectedTypeCodes],
  );

  return (
    <Box>
      <Typography sx={{ mb: 2, fontWeight: 500, fontSize: 15, color: 'text.primary' }}>
        Required Documents
      </Typography>
      <Grid container spacing={5}>
        {columns.map((column) => (
          <Grid key={column.key} size="auto" sx={{ width: 200 }}>
            <Typography
              sx={{
                mb: 1,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: 'text.disabled',
              }}
            >
              {column.heading}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {column.documents.map((doc) => (
                <Box key={doc} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Neutral 6px dot — matches the StatusBadge indicator; gray for info-only. */}
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: 14, color: 'text.primary' }}>{doc}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

export default RequiredDocumentsList;
